// Menu button (WAI-ARIA menu pattern): arrow keys move focus, Escape closes and returns
// focus to the trigger, Tab closes (focus continues from the trigger).
// The menu is portalled to <body> (or to the open <dialog> holding the trigger, so it stays in the top layer and is
// not made inert) and positioned with `position: fixed`, flipping to stay in the viewport: an overflow-clipping
// container such as the navigation rail can never cut it (D36).
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { FLOATING_OWNER_ATTR, placeFloating, type FloatingPlacement, type FloatingPosition } from "./floating.ts";

export interface MenuItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  /** Visible reason shown under a disabled item. */
  hint?: ReactNode;
  checked?: boolean;
  kind?: "item" | "separator" | "label";
}

interface MenuProps {
  trigger: (props: {
    ref: (el: HTMLButtonElement | null) => void;
    "aria-haspopup": "menu";
    "aria-expanded": boolean;
    "aria-controls": string;
    onClick: () => void;
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  }) => ReactNode;
  items: MenuItem[];
  label: string;
  placement?: FloatingPlacement;
  /** Distance in px between the trigger and the menu (e.g. larger to clear the navigation rail's edge). */
  gap?: number;
  /** Called when the menu opens (e.g. to fetch server-provided items). */
  onOpen?: () => void;
}

export function Menu({ trigger, items, label, placement = "down-end", gap = 4, onOpen }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<FloatingPosition | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const wrapId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const contains = useCallback((node: Node | null) => !!node && (!!wrapRef.current?.contains(node) || !!menuRef.current?.contains(node)), []);

  const focusable = useCallback(
    () => items.map((it, i) => ({ it, i })).filter(({ it }) => (it.kind ?? "item") === "item" && !it.disabled),
    [items],
  );

  const focusIndex = useCallback((pos: number) => {
    itemRefs.current[pos]?.focus();
  }, []);

  const focusableRef = useRef(focusable);
  focusableRef.current = focusable;

  const openMenu = () => {
    onOpen?.();
    // An open modal <dialog> makes everything outside it inert, so there the menu lives inside that dialog.
    setHost(triggerRef.current?.closest("dialog") ?? document.body);
    setPosition(null);
    setOpen(true);
  };

  // Placed before paint, then kept next to the trigger on scroll, resize and changes of the menu's own size.
  useLayoutEffect(() => {
    if (!open || !host) return;
    const place = () => {
      const triggerEl = triggerRef.current;
      const menu = menuRef.current;
      if (!triggerEl || !menu) return;
      const next = placeFloating(
        triggerEl.getBoundingClientRect(),
        { width: menu.offsetWidth, height: menu.scrollHeight },
        { width: window.innerWidth, height: window.innerHeight },
        placement,
        gap,
      );
      setPosition((prev) => (prev && prev.top === next.top && prev.left === next.left && prev.maxHeight === next.maxHeight && prev.side === next.side ? prev : next));
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(place) : null;
    if (menuRef.current) observer?.observe(menuRef.current);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      observer?.disconnect();
    };
  }, [open, host, placement, gap]);

  // Focus the first item once when the menu opens; later item updates never move focus.
  useEffect(() => {
    if (!open) return;
    const first = focusableRef.current()[0];
    if (first) requestAnimationFrame(() => focusIndex(first.i));
  }, [open, focusIndex]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!contains(e.target as Node)) setOpen(false);
    };
    // Escape closes even before focus has moved into the menu.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && contains(document.activeElement)) {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, contains]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = focusable();
    const current = list.findIndex(({ i }) => itemRefs.current[i] === document.activeElement);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (list.length === 0) return;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const next = list[(current + delta + list.length) % list.length]!;
      focusIndex(next.i);
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      const target = e.key === "Home" ? list[0] : list[list.length - 1];
      if (target) focusIndex(target.i);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === "Tab") {
      // The menu sits at the end of <body>: focus goes back to the trigger so Tab continues from there.
      close(true);
    }
  };

  return (
    <div className="menu-wrap" ref={wrapRef} id={wrapId}>
      {trigger({
        ref: (el) => {
          triggerRef.current = el;
        },
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": menuId,
        onClick: () => {
          if (open) setOpen(false);
          else openMenu();
        },
        onKeyDown: (e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            if (!open) {
              e.preventDefault();
              openMenu();
            }
          } else if (e.key === "Escape" && open) {
            // Handled here so a container (e.g. the navigation overlay) does not also react to this Escape.
            e.preventDefault();
            e.stopPropagation();
            close(true);
          }
        },
      })}
      {open && host
        ? createPortal(
            <div
              id={menuId}
              ref={menuRef}
              role="menu"
              aria-label={label}
              className={`menu menu--${placement}`}
              data-side={position?.side}
              {...{ [FLOATING_OWNER_ATTR]: wrapId }}
              style={menuStyle(position)}
              onKeyDown={onMenuKeyDown}
            >
              {items.map((item, i) => {
                const kind = item.kind ?? "item";
                if (kind === "separator") return <div key={item.id} role="separator" className="menu-separator" />;
                if (kind === "label")
                  return (
                    <div key={item.id} className="menu-label" role="presentation">
                      {item.label}
                    </div>
                  );
                const role = item.checked === undefined ? "menuitem" : "menuitemradio";
                return (
                  <button
                    key={item.id}
                    ref={(el) => {
                      itemRefs.current[i] = el;
                    }}
                    type="button"
                    role={role}
                    aria-checked={item.checked === undefined ? undefined : item.checked}
                    tabIndex={-1}
                    className="menu-item"
                    disabled={item.disabled}
                    aria-disabled={item.disabled || undefined}
                    onClick={() => {
                      close(true);
                      item.onSelect?.();
                    }}
                  >
                    {item.icon}
                    <span className="menu-item-text">
                      <span className="menu-item-label">{item.label}</span>
                      {item.hint ? <span className="menu-item-hint">{item.hint}</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>,
            host,
          )
        : null}
    </div>
  );
}

/** Hidden (but measurable) until placed, so it never flashes at the wrong spot. */
function menuStyle(position: FloatingPosition | null): CSSProperties {
  if (!position) return { top: 0, left: 0, visibility: "hidden" };
  return { top: position.top, left: position.left, maxHeight: position.maxHeight };
}
